import { useEffect, useState } from "react";
import { usePriorities } from "@/context/PrioritiesContext";
import { useClient } from "@/context/ClientContext";
import LeadsReports from "@/pages/LeadsReports";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend,
} from "recharts";
import { BarChart3, Mail, TrendingUp, Shield, RefreshCw, Sparkles, Building2, UserCog, Heart, MessageSquare, CalendarClock } from "lucide-react";
import EmailDeliveryDashboard from "@/components/reports/EmailDeliveryDashboard";
import VolumeTrendsReport from "@/components/reports/VolumeTrendsReport";
import SlaPerformanceReport from "@/components/reports/SlaPerformanceReport";
import {
  DeptPerformanceReport, AgentProductivityReport, CxReport, CommsReport,
} from "@/components/reports/AdvancedReports";
import CustomReportsTab from "@/components/reports/CustomReportsTab";
import ScheduledReportsTab from "@/components/reports/ScheduledReportsTab";

const STATUS_COLORS = { open: "#2563eb", in_progress: "#d97706", pending_user: "#9333ea", resolved: "#059669", closed: "#64748b" };
const PRIORITY_COLORS = { low: "#94a3b8", medium: "#2563eb", high: "#ea580c", critical: "#e11d48" };
const PIE_COLORS = ["#4f46e5", "#059669", "#ea580c", "#9333ea", "#0ea5e9", "#e11d48", "#64748b", "#d97706"];

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const DEFAULT_FILTERS = () => ({
  date_from: daysAgoIso(29),
  date_to: todayIso(),
  department_id: "all",
  channel: "all",
  priority: "all",
});

export default function Reports() {
  const { active } = useClient();
  if (active?.kind === "leads") return <LeadsReports />;
  return <TicketingReports />;
}

function TicketingReports() {
  const { priorityOptions } = usePriorities();
  const [filters, setFilters] = useState(DEFAULT_FILTERS());
  const [granularity, setGranularity] = useState("day");
  const [departments, setDepartments] = useState([]);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data.filter((d) => d.active)));
    api.get("/channels").then((r) => setChannels(r.data));
  }, []);

  const reset = () => setFilters(DEFAULT_FILTERS());

  return (
    <Tabs defaultValue="overview" data-testid="reports-page" className="space-y-5">
      <TabsList className="bg-slate-100 border border-slate-200 flex-wrap h-auto">
        <TabsTrigger value="overview" className="data-[state=active]:bg-white" data-testid="reports-tab-overview">
          <BarChart3 className="w-4 h-4 mr-1.5" /> Overview
        </TabsTrigger>
        <TabsTrigger value="volume" className="data-[state=active]:bg-white" data-testid="reports-tab-volume">
          <TrendingUp className="w-4 h-4 mr-1.5" /> Volume &amp; Trends
        </TabsTrigger>
        <TabsTrigger value="sla" className="data-[state=active]:bg-white" data-testid="reports-tab-sla">
          <Shield className="w-4 h-4 mr-1.5" /> SLA &amp; Performance
        </TabsTrigger>
        <TabsTrigger value="depts" className="data-[state=active]:bg-white" data-testid="reports-tab-depts">
          <Building2 className="w-4 h-4 mr-1.5" /> Departments
        </TabsTrigger>
        <TabsTrigger value="agents" className="data-[state=active]:bg-white" data-testid="reports-tab-agents">
          <UserCog className="w-4 h-4 mr-1.5" /> Agents
        </TabsTrigger>
        <TabsTrigger value="cx" className="data-[state=active]:bg-white" data-testid="reports-tab-cx">
          <Heart className="w-4 h-4 mr-1.5" /> CX
        </TabsTrigger>
        <TabsTrigger value="comms" className="data-[state=active]:bg-white" data-testid="reports-tab-comms">
          <MessageSquare className="w-4 h-4 mr-1.5" /> Comms
        </TabsTrigger>
        <TabsTrigger value="email" className="data-[state=active]:bg-white" data-testid="reports-tab-email">
          <Mail className="w-4 h-4 mr-1.5" /> Email Delivery
        </TabsTrigger>
        <TabsTrigger value="custom" className="data-[state=active]:bg-white" data-testid="reports-tab-custom">
          <Sparkles className="w-4 h-4 mr-1.5" /> Custom
        </TabsTrigger>
        <TabsTrigger value="schedules" className="data-[state=active]:bg-white" data-testid="reports-tab-schedules">
          <CalendarClock className="w-4 h-4 mr-1.5" /> Schedules
        </TabsTrigger>
      </TabsList>

      {/* Shared filter ribbon (applies to Volume + SLA tabs) */}
      <Card className="border-slate-200" data-testid="reports-filter-ribbon">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1 h-9 w-36 text-xs" data-testid="reports-filter-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1 h-9 w-36 text-xs" data-testid="reports-filter-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Department</Label>
            <Select value={filters.department_id} onValueChange={(v) => setFilters({ ...filters, department_id: v })}>
              <SelectTrigger className="mt-1 h-9 w-40 text-xs" data-testid="reports-filter-dept"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Channel</Label>
            <Select value={filters.channel} onValueChange={(v) => setFilters({ ...filters, channel: v })}>
              <SelectTrigger className="mt-1 h-9 w-32 text-xs" data-testid="reports-filter-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {channels.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Priority</Label>
            <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
              <SelectTrigger className="mt-1 h-9 w-28 text-xs" data-testid="reports-filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {priorityOptions.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" size="sm" onClick={reset} className="text-slate-500 ml-auto" data-testid="reports-filter-reset">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reset
          </Button>
        </CardContent>
      </Card>

      <TabsContent value="overview" className="mt-2">
        <TicketOverviewCharts />
      </TabsContent>
      <TabsContent value="volume" className="mt-2">
        <VolumeTrendsReport filters={filters} granularity={granularity} onGranularity={setGranularity} />
      </TabsContent>
      <TabsContent value="sla" className="mt-2">
        <SlaPerformanceReport filters={filters} />
      </TabsContent>
      <TabsContent value="depts" className="mt-2">
        <DeptPerformanceReport filters={filters} />
      </TabsContent>
      <TabsContent value="agents" className="mt-2">
        <AgentProductivityReport filters={filters} />
      </TabsContent>
      <TabsContent value="cx" className="mt-2">
        <CxReport filters={filters} />
      </TabsContent>
      <TabsContent value="comms" className="mt-2">
        <CommsReport filters={filters} />
      </TabsContent>
      <TabsContent value="email" className="mt-2">
        <EmailDeliveryDashboard />
      </TabsContent>
      <TabsContent value="custom" className="mt-2">
        <CustomReportsTab />
      </TabsContent>
      <TabsContent value="schedules" className="mt-2">
        <ScheduledReportsTab />
      </TabsContent>
    </Tabs>
  );
}

// Preserved from original Reports page — the 3 simple bar/pie charts.
function TicketOverviewCharts() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setStats(r.data)); }, []);

  const byStatus = (stats?.by_status || []).map((s) => ({ name: s.status.replace("_", " "), count: s.count, key: s.status }));
  const byPriority = (stats?.by_priority || []).map((p) => ({ name: p.priority, count: p.count, key: p.priority }));
  const byChannel = (stats?.by_channel || []).map((c) => ({ name: c.channel, value: c.count }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-heading text-base">Tickets by Status</CardTitle></CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer>
            <BarChart data={byStatus}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip cursor={{ fill: "rgba(15,23,42,0.04)" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {byStatus.map((e) => (<Cell key={e.key} fill={STATUS_COLORS[e.key] || "#4f46e5"} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="font-heading text-base">Tickets by Priority</CardTitle></CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer>
            <BarChart data={byPriority}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip cursor={{ fill: "rgba(15,23,42,0.04)" }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {byPriority.map((e) => (<Cell key={e.key} fill={PRIORITY_COLORS[e.key] || "#4f46e5"} />))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="border-slate-200 lg:col-span-2">
        <CardHeader><CardTitle className="font-heading text-base">Volume by Channel</CardTitle></CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={byChannel} dataKey="value" nameKey="name" outerRadius={110} innerRadius={60} paddingAngle={2}>
                {byChannel.map((c, i) => (<Cell key={c.name || `c-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
